import { useState, useEffect, useCallback } from 'react';
import api from '@/middlewares/api';

/**
 * Normalise the GET /v1/user/credits payload.
 * `credits` is the balance that gates the membership discount; the
 * `credit_discounted_*` keys are the legacy shape of the same counters.
 * @param {Object} [data] Raw response body
 * @returns {{hasSubscription: boolean, sampleClearances: number, songsAnalyzed: number}}
 */
export const mapCreditsResponse = (data) => {
    const balances = data && data.credits;

    return {
        hasSubscription: Boolean(data && data.has_subscription),
        sampleClearances:
            (balances ? balances.sample_clearances : data && data.credit_discounted_sample_clearances) || 0,
        songsAnalyzed:
            (balances ? balances.songs_analyzed : data && data.credit_discounted_songs_analyzed) || 0
    };
};

/**
 * Custom hook to fetch user credits
 * @returns {Object} { credits, loading, error, refetch }
 * A custom hook to fetch and manage user credits.
 * @returns {{
 *   credits: {hasSubscription: boolean, sampleClearances: number, songsAnalyzed: number},
 *   loading: boolean,
 *   error: any
 * }}
 */
const useUserCredits = () => {
    const [credits, setCredits] = useState({
        hasSubscription: false,
        sampleClearances: 0,
        songsAnalyzed: 0
    });
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    const fetchCredits = useCallback(async () => {
        try {
            setLoading(true);
            setError(null);

            const response = await api.get('user/credits');

            setCredits(mapCreditsResponse(response.data));
        } catch (err) {
            console.error('Error fetching user credits:', err);
            setError(err.response?.data?.message || 'Failed to fetch user credits');
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        fetchCredits();
    }, [fetchCredits]);

    return {
        credits,
        loading,
        error,
        refetch: fetchCredits
    };
};

export default useUserCredits;
